import {
    getDockZones,
    findDockSide,
    attachToDockStrip,
    detachFromDockStrip,
    maxAnchoredHeight,
    getSideWidth,
    setSideWidth,
    notifyDockResize,
} from "./dock-strip.js";

const STYLE_ID = "ls-dock-style";

const MIN_SIDE_ANCHORED_HEIGHT = 120;

const STYLE = `
    .ls-dock {
        position: fixed;
        display: none;
        flex-direction: column;
        box-sizing: border-box;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        z-index: 10001;
    }
    .ls-dock.is-open {
        display: flex;
    }
    /* An author display rule beats the browser's own [hidden] rule, so
       anything styled inside a dock would ignore being hidden. */
    .ls-dock [hidden] {
        display: none !important;
    }
    .ls-dock.is-dragging {
        user-select: none;
    }
    .ls-dock-head {
        user-select: none;
        display: flex;
        align-items: center;
        width: 100%;
        height: 42px;
        flex-shrink: 0;
        background: linear-gradient(135deg, #855cd6 0%, #6b4bb5 100%);
        padding-inline: 12px;
        cursor: grab;
        border-radius: 12px 12px 0 0;
        gap: 6px;
        touch-action: none;
    }
    .ls-dock.is-dragging .ls-dock-head {
        cursor: grabbing;
    }
    .ls-dock-head-text {
        font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
        font-weight: bold;
        font-size: 15px;
        color: #fff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .ls-dock-head-filler {
        flex-grow: 1;
    }
    .ls-dock-head-btn {
        cursor: pointer;
        border: none;
        border-radius: 100%;
        padding: 4px;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        background-color: rgba(255, 255, 255, 0.18);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease;
    }
    .ls-dock-head-btn svg {
        width: 14px;
        height: 14px;
    }
    .ls-dock-head-btn:hover {
        background-color: rgba(255, 255, 255, 0.32);
    }
    .ls-dock-head-btn.is-active {
        background-color: rgba(255, 255, 255, 0.55);
    }
    .ls-dock-body {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: auto;
    }
    /* Lets a window shrink to its content instead of leaving empty space
       below it, while it has nothing tall to show. */
    .ls-dock.is-auto-height:not(.is-anchored):not(.is-expanded) {
        height: auto !important;
        min-height: 0 !important;
        /* Inline resize:both would otherwise offer a vertical handle that
           the auto height ignores. */
        resize: horizontal !important;
    }
    .ls-dock.is-expanded {
        left: 0 !important;
        top: 0 !important;
        transform: none !important;
        width: 100vw !important;
        height: 100vh !important;
        height: 100dvh !important;
        /* The inline min/max sizes of the floating window would otherwise
           clamp this back down to its normal size. */
        min-width: 0 !important;
        min-height: 0 !important;
        max-width: none !important;
        max-height: none !important;
        border-radius: 0;
        background: rgba(0, 0, 0, 0.78);
        box-shadow: none;
        resize: none !important;
        z-index: 10002;
    }
    .ls-dock.is-expanded .ls-dock-head {
        background: transparent;
        border-radius: 0;
    }
    .ls-dock.is-anchored {
        position: relative !important;
        left: auto !important;
        top: auto !important;
        transform: none !important;
        flex: var(--ls-dock-grow, 1) 1 var(--ls-dock-width, 0px);
        min-width: 240px;
        width: auto !important;
        max-width: none !important;
        height: var(--ls-dock-height, auto) !important;
        min-height: 0 !important;
        max-height: var(--ls-dock-max-height, 50vh) !important;
        resize: none !important;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    }
    /* No cameras means nothing tall to show, so the window keeps no empty room
       even if its height was dragged while there were any. */
    .ls-dock.is-auto-height.is-anchored {
        height: auto !important;
    }
    .ls-dock-grip {
        display: none;
    }
    .ls-dock.is-anchored .ls-dock-grip {
        display: block;
        position: absolute;
        right: 0;
        bottom: 0;
        width: 18px;
        height: 18px;
        cursor: nwse-resize;
        touch-action: none;
        background: linear-gradient(
            135deg,
            transparent 55%,
            rgba(133, 92, 214, 0.45) 55%
        );
    }
    .ls-dock.is-anchored .ls-dock-grip:hover {
        background: linear-gradient(135deg, transparent 55%, #855cd6 55%);
    }
    /* Docked below the workspace the window grows upwards, so its grip sits
       on the top corner. */
    .ls-dock.is-anchored-bottom .ls-dock-grip {
        top: 0;
        bottom: auto;
        cursor: nesw-resize;
        background: linear-gradient(
            45deg,
            transparent 55%,
            rgba(133, 92, 214, 0.45) 55%
        );
    }
    .ls-dock.is-anchored-bottom .ls-dock-grip:hover {
        background: linear-gradient(45deg, transparent 55%, #855cd6 55%);
    }
    /* Docked at the side, the windows stack in a column that sets their
       width, and share its height. */
    .ls-dock.is-anchored.is-anchored-right {
        flex: var(--ls-dock-grow, 1) 1 var(--ls-dock-height, 0px);
        min-width: 0;
        width: 100% !important;
        height: auto !important;
        min-height: ${MIN_SIDE_ANCHORED_HEIGHT}px !important;
        max-height: none !important;
    }
    .ls-dock.is-auto-height.is-anchored.is-anchored-right {
        flex: 0 0 auto;
        min-height: 0 !important;
    }
    .ls-dock.is-anchored-right .ls-dock-grip {
        left: 0;
        right: auto;
        cursor: nesw-resize;
        background: linear-gradient(
            225deg,
            transparent 55%,
            rgba(133, 92, 214, 0.45) 55%
        );
    }
    .ls-dock.is-anchored-right .ls-dock-grip:hover {
        background: linear-gradient(225deg, transparent 55%, #855cd6 55%);
    }
    .ls-dock.is-resizing {
        user-select: none;
    }
    .ls-dock.is-anchored .ls-dock-head {
        border-radius: 8px 8px 0 0;
    }
    #ls-dock-highlight {
        position: fixed;
        pointer-events: none;
        z-index: 10000;
        border: 2px dashed #855cd6;
        background: rgba(133, 92, 214, 0.12);
        border-radius: 8px;
        opacity: 0;
        transition: opacity 0.1s ease;
    }
`;

const ICONS = {
    pin: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79L16 12V5h1a1 1 0 0 0 0-2H7a1 1 0 0 0 0 2h1v7l-1.89 1.45A2 2 0 0 0 5 15.24z"/></svg>`,
    expand: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`,
    collapse: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`,
    close: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
};

const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
};

let highlightEl = null;
const setDockHighlight = (side) => {
    const zone = side && getDockZones()?.[side];

    if (!zone) {
        if (highlightEl) highlightEl.style.opacity = "0";
        return;
    }

    if (!highlightEl) {
        highlightEl = document.createElement("div");
        highlightEl.id = "ls-dock-highlight";
        document.body.appendChild(highlightEl);
    }

    highlightEl.style.left = `${zone.left + 4}px`;
    highlightEl.style.top = `${zone.top + 4}px`;
    highlightEl.style.width = `${zone.right - zone.left - 8}px`;
    highlightEl.style.height = `${zone.bottom - zone.top - 8}px`;
    highlightEl.style.opacity = "1";
};

const MIN_ANCHORED_WIDTH = 240;
const MIN_ANCHORED_HEIGHT = 90;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pointFromEvent = (e) => {
    const t = e.touches?.[0] || e.changedTouches?.[0];
    return t ? { x: t.clientX, y: t.clientY } : { x: e.clientX, y: e.clientY };
};

export default class DockableWindow {
    constructor({
        id,
        title,
        width = 320,
        height = 440,
        minWidth = 260,
        minHeight = 200,
        maxWidth = 640,
        maxHeight = "80vh",
        anchoredFill = false,
        resizable = true,
        expandable = false,
        onClose,
        onAnchorChange,
        onExpandChange,
    } = {}) {
        ensureStyles();

        this.id = id;
        this.anchored = false;
        this.anchorSide = null;
        this.lastAnchorSide = null;
        this.expanded = false;
        this.userPosition = null;
        this.floatingPosition = null;
        this.anchoredFill = anchoredFill;
        this.anchoredResized = false;
        this.resizable = resizable;
        this.onClose = onClose;
        this.onAnchorChange = onAnchorChange;
        this.onExpandChange = onExpandChange;

        this.rootEl = document.createElement("div");
        this.rootEl.id = id;
        this.rootEl.className = "ls-dock";
        this.rootEl.style.width = `${width}px`;
        this.rootEl.style.height = `${height}px`;
        this.rootEl.style.minWidth = `${minWidth}px`;
        this.rootEl.style.minHeight = `${minHeight}px`;
        this.rootEl.style.maxWidth =
            typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;
        this.rootEl.style.maxHeight =
            typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
        if (resizable) this.rootEl.style.resize = "both";

        this.headerEl = document.createElement("div");
        this.headerEl.className = "ls-dock-head";

        const titleEl = document.createElement("span");
        titleEl.className = "ls-dock-head-text";
        titleEl.textContent = title || "";

        const filler = document.createElement("div");
        filler.className = "ls-dock-head-filler";

        this.headerEl.append(titleEl, filler);

        this.expandBtn = null;
        if (expandable) {
            this.expandBtn = this._makeHeadButton(ICONS.expand, "Ampliar", () =>
                this.toggleExpanded(),
            );
            this.headerEl.appendChild(this.expandBtn);
        }

        this.pinBtn = this._makeHeadButton(ICONS.pin, "Anclar arriba", () =>
            this.toggleAnchored(),
        );
        this.headerEl.appendChild(this.pinBtn);

        this.closeBtn = this._makeHeadButton(ICONS.close, "Cerrar", () =>
            this.close(),
        );
        this.headerEl.appendChild(this.closeBtn);

        this.bodyEl = document.createElement("div");
        this.bodyEl.className = "ls-dock-body";

        this.gripEl = document.createElement("div");
        this.gripEl.className = "ls-dock-grip";
        this.gripEl.title = "Arrastra para cambiar el tamaño";

        this.rootEl.append(this.headerEl, this.bodyEl, this.gripEl);
        document.body.appendChild(this.rootEl);

        this._enableDrag();
        this._enableAnchoredResize();

        window.addEventListener("resize", () => this._applyAnchoredHeight());
    }

    _applyAnchoredHeight() {
        if (!this.anchored) return;

        // Keeps the column within what the workspace can spare after a resize
        if (this.anchorSide === "right") {
            setSideWidth(getSideWidth());
            return;
        }

        const max = maxAnchoredHeight(this.anchorSide);

        this.rootEl.style.setProperty("--ls-dock-max-height", `${max}px`);

        if (this.anchoredResized) return;

        if (this.anchoredFill)
            this.rootEl.style.setProperty("--ls-dock-height", `${max}px`);
        else this.rootEl.style.removeProperty("--ls-dock-height");
    }

    _enableAnchoredResize() {
        let resizing = false;
        let startX = 0;
        let startY = 0;
        let startWidth = 0;
        let startHeight = 0;
        let startSideWidth = 0;

        const onMove = (e) => {
            if (!resizing) return;
            const p = pointFromEvent(e);

            const dx = p.x - startX;
            const dy = p.y - startY;

            if (this.anchorSide === "right") {
                // The grip faces the workspace, so dragging left widens
                if (Math.abs(dx) > 2) setSideWidth(startSideWidth - dx);

                if (Math.abs(dy) > 2) {
                    const height = Math.max(
                        MIN_SIDE_ANCHORED_HEIGHT,
                        startHeight + dy,
                    );
                    this.rootEl.style.setProperty(
                        "--ls-dock-height",
                        `${height}px`,
                    );
                    this.rootEl.style.setProperty("--ls-dock-grow", 0);
                }
            } else {
                if (Math.abs(dx) > 2) {
                    const width = Math.max(MIN_ANCHORED_WIDTH, startWidth + dx);
                    this.rootEl.style.setProperty(
                        "--ls-dock-width",
                        `${width}px`,
                    );
                    this.rootEl.style.setProperty("--ls-dock-grow", 0);
                }

                if (Math.abs(dy) > 2) {
                    // Docked at the bottom, dragging up makes it taller
                    const growth = this.anchorSide === "bottom" ? -dy : dy;
                    const height = clamp(
                        startHeight + growth,
                        MIN_ANCHORED_HEIGHT,
                        maxAnchoredHeight(this.anchorSide),
                    );
                    this.rootEl.style.setProperty(
                        "--ls-dock-height",
                        `${height}px`,
                    );
                    this.anchoredResized = true;
                }
            }

            notifyDockResize();
            e.preventDefault?.();
        };

        const onEnd = () => {
            if (!resizing) return;
            resizing = false;
            this.rootEl.classList.remove("is-resizing");
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onEnd);
            document.removeEventListener("touchmove", onMove);
            document.removeEventListener("touchend", onEnd);
            document.removeEventListener("touchcancel", onEnd);
            notifyDockResize();
        };

        const onStart = (e) => {
            const p = pointFromEvent(e);
            const rect = this.rootEl.getBoundingClientRect();
            startX = p.x;
            startY = p.y;
            startWidth = rect.width;
            startHeight = rect.height;
            startSideWidth = getSideWidth();

            resizing = true;
            this.rootEl.classList.add("is-resizing");
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onEnd);
            document.addEventListener("touchmove", onMove, { passive: false });
            document.addEventListener("touchend", onEnd);
            document.addEventListener("touchcancel", onEnd);
            e.preventDefault?.();
            e.stopPropagation();
        };

        this.gripEl.addEventListener("mousedown", onStart);
        this.gripEl.addEventListener("touchstart", onStart, { passive: false });
    }

    _makeHeadButton(icon, title, onClick) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ls-dock-head-btn";
        btn.title = title;
        btn.dataset.dockNoDrag = "true";
        btn.innerHTML = icon;
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            onClick();
        });
        return btn;
    }

    _enableDrag() {
        let dragging = false;
        let dockSide = null;
        let startX, startY, startLeft, startTop;

        const onMove = (e) => {
            if (!dragging) return;
            const p = pointFromEvent(e);
            const width = this.rootEl.offsetWidth;
            let left = startLeft + (p.x - startX);
            let top = startTop + (p.y - startY);
            left = Math.max(-width + 80, Math.min(left, window.innerWidth - 80));
            top = Math.max(0, Math.min(top, window.innerHeight - 40));

            this.rootEl.style.left = `${left}px`;
            this.rootEl.style.top = `${top}px`;
            this.userPosition = { left, top };

            dockSide = findDockSide(p.x, p.y);
            setDockHighlight(dockSide);

            e.preventDefault?.();
        };

        const onEnd = () => {
            if (!dragging) return;
            dragging = false;
            this.rootEl.classList.remove("is-dragging");
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onEnd);
            document.removeEventListener("touchmove", onMove);
            document.removeEventListener("touchend", onEnd);
            setDockHighlight(null);
            if (dockSide) this.setAnchored(true, { side: dockSide });
        };

        const onStart = (e) => {
            if (e.target.closest("[data-dock-no-drag]")) return;

            const p = pointFromEvent(e);

            if (this.anchored) {
                this.setAnchored(false, {
                    position: { left: p.x - 40, top: p.y - 16 },
                });
            } else if (this.expanded) {
                this.setExpanded(false);
            }

            dragging = true;
            dockSide = null;
            startX = p.x;
            startY = p.y;
            const rect = this.rootEl.getBoundingClientRect();
            startLeft = rect.left;
            startTop = rect.top;

            this.rootEl.classList.add("is-dragging");
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onEnd);
            document.addEventListener("touchmove", onMove, { passive: false });
            document.addEventListener("touchend", onEnd);
            e.preventDefault?.();
        };

        this.headerEl.addEventListener("mousedown", onStart);
        this.headerEl.addEventListener("touchstart", onStart, { passive: false });
    }

    positionNear(launcherEl) {
        if (this.userPosition || this.anchored || !launcherEl) return;

        const btnRect = launcherEl.getBoundingClientRect();
        const width = this.rootEl.offsetWidth || parseFloat(this.rootEl.style.width) || 320;
        let left = btnRect.right + 12;
        if (left + width > window.innerWidth - 10) {
            left = btnRect.left - width - 12;
        }
        left = Math.max(10, left);
        const top = Math.min(btnRect.top, window.innerHeight - 200);

        this.rootEl.style.left = `${left}px`;
        this.rootEl.style.top = `${top}px`;
    }

    setAnchored(anchored, { side = "top", position } = {}) {
        if (anchored === this.anchored) return;
        this.anchored = anchored;

        if (anchored) {
            if (this.expanded) this.setExpanded(false);
            this.floatingPosition = {
                left: this.rootEl.style.left,
                top: this.rootEl.style.top,
            };

            // A size picked on one side means nothing on another
            if (side !== this.lastAnchorSide) this._resetAnchoredSize();
            this.anchorSide = side;
            this.lastAnchorSide = side;
            this.rootEl.classList.add(`is-anchored-${side}`);

            attachToDockStrip(this.rootEl, side);
            this._applyAnchoredHeight();
        } else {
            this.rootEl.classList.remove(`is-anchored-${this.anchorSide}`);
            this.anchorSide = null;

            detachFromDockStrip(this.rootEl);
            const pos = position || this.floatingPosition;
            if (pos) {
                this.rootEl.style.left =
                    typeof pos.left === "number" ? `${pos.left}px` : pos.left || "";
                this.rootEl.style.top =
                    typeof pos.top === "number" ? `${pos.top}px` : pos.top || "";
                this.userPosition = { left: pos.left, top: pos.top };
            }
        }

        this.rootEl.classList.toggle("is-anchored", anchored);
        this.pinBtn.classList.toggle("is-active", anchored);
        this.pinBtn.title = anchored ? "Desanclar" : "Anclar arriba";
        if (this.expandBtn) this.expandBtn.hidden = anchored;

        this.onAnchorChange?.(anchored);
    }

    toggleAnchored() {
        this.setAnchored(!this.anchored);
    }

    isAnchored() {
        return this.anchored;
    }

    getAnchorSide() {
        return this.anchorSide;
    }

    _resetAnchoredSize() {
        ["--ls-dock-width", "--ls-dock-grow", "--ls-dock-height"].forEach(
            (name) => this.rootEl.style.removeProperty(name),
        );
        this.anchoredResized = false;
    }

    setExpanded(expanded) {
        if (expanded === this.expanded) return;
        if (expanded && this.anchored) this.setAnchored(false);

        this.expanded = expanded;
        this.rootEl.classList.toggle("is-expanded", expanded);
        if (this.expandBtn) {
            this.expandBtn.innerHTML = expanded ? ICONS.collapse : ICONS.expand;
            this.expandBtn.title = expanded ? "Restaurar tamaño" : "Ampliar";
            this.expandBtn.classList.toggle("is-active", expanded);
        }

        this.onExpandChange?.(expanded);
    }

    toggleExpanded() {
        this.setExpanded(!this.expanded);
    }

    setAutoHeight(auto) {
        this.rootEl.classList.toggle("is-auto-height", auto);
    }

    isExpanded() {
        return this.expanded;
    }

    open() {
        this.rootEl.classList.add("is-open");
        if (this.anchored) notifyDockResize();
    }

    close() {
        this.rootEl.classList.remove("is-open");
        if (this.anchored) notifyDockResize();
        this.onClose?.();
    }

    toggle() {
        if (this.isOpen()) this.close();
        else this.open();
    }

    isOpen() {
        return this.rootEl.classList.contains("is-open");
    }

    destroy() {
        if (this.anchored) detachFromDockStrip(this.rootEl);
        this.rootEl.remove();
    }
}
